import {TitleBox} from "../general/TitleBox";
import {useAuth} from "../auth/AuthHook";
import {Empty, Table} from "antd";
import {LockIcon} from "../components/Icons";
import React, {ReactPropTypes, useEffect, useMemo, useState} from "react";
import {useSupportCase} from "../support/CaseHook";
import {useTranslation} from "react-i18next";
import '../filter/CaseFilter.css';

interface CategorizationResult {
    doc_id: string;
    parent_doc_id?: string;
    url: string;
    product: string;
    version: string;
    explanation: string;
}

const ConfluenceIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className={"size-6"}>
        <path fill="#1868DB"
              d="M5.6 34.2c-.4.7-.9 1.5-1.3 2.1-.4.6-.2 1.4.4 1.8l7.6 4.6c.6.4 1.4.2 1.8-.4.4-.6.8-1.3 1.2-2 3.2-5.7 6.6-5 12.8-2.2l7.7 3.5c.7.3 1.4 0 1.7-.6l3.8-8.4c.3-.7 0-1.4-.6-1.7-2.2-1-6.5-3-8.1-3.7C20.6 22.6 11.2 24.1 5.6 34.2z"/>
        <path fill="#1868DB" opacity=".7"
              d="M42.4 13.8c.4-.7.9-1.5 1.3-2.1.4-.6.2-1.4-.4-1.8L35.7 5.3c-.6-.4-1.4-.2-1.8.4-.4.6-.8 1.3-1.2 2-3.2 5.7-6.6 5-12.8 2.2l-7.7-3.5c-.7-.3-1.4 0-1.7.6l-3.8 8.4c-.3.7 0 1.4.6 1.7 2.2 1 6.5 3 8.1 3.7 12 5.6 21.4 4.1 27-5.9z"/>
    </svg>
);

export function ConfluenceAdmin() {
    const {isLoggedIn, isSysAdmin} = useAuth();
    const {fetchData} = useSupportCase();
    const {t} = useTranslation();
    const [explanationList, setExplanationList] = useState<CategorizationResult[]>([]);
    const [loading, setLoading] = useState(false);

    const productFilters = useMemo(() =>
        [...new Set(explanationList.map(r => r.product))].sort().map(p => ({text: p, value: p})),
        [explanationList]);

    const versionFilters = useMemo(() =>
        [...new Set(explanationList.map(r => r.version))].sort().map(v => ({text: v, value: v})),
        [explanationList]);

    useEffect(() => {
        const fetchConfluenceAdminData = async () => {
            if (!isLoggedIn() || !isSysAdmin) {
                return;
            }
            setLoading(true);
            try {
                const res = await fetchData("integrations/confluence/categorizations", "GET");
                const data = await res.json() as CategorizationResult[];
                setExplanationList(data);
            } catch (e) {
                // Handle error as needed
            } finally {
                setLoading(false);
            }
        };
        fetchConfluenceAdminData();
    }, [isLoggedIn, isSysAdmin, fetchData]);



    return (
        <div className="w-full lg:px-16 xl:px-32">
            <TitleBox lineHidden={true}>
                <h1 className="flex text-2xl font-bold py-8 items-center justify-center gap-2">
                    <ConfluenceIcon />
                    {t("Confluence Categorizations")}
                </h1>
            </TitleBox>
            {!isLoggedIn() && (
                <Empty
                    description={
                        <span className="text-gray-300 text-base">
                            {t("You need to log in as sysadmin to see Confluence categorization results.")}
                        </span>
                    }
                    image={<LockIcon />}
                />
            )}
            {isLoggedIn() && !isSysAdmin && (
                <Empty
                    description={
                        <span className="text-gray-300 text-base">
                            {t("You need to log in as sysadmin to see Confluence categorization results.")}
                        </span>
                    }
                    image={<LockIcon />}
                />
            )}
            {isLoggedIn() && isSysAdmin && (
                <div className={"mb-6 flex flex-col"}>
                    <Table<CategorizationResult>
                        dataSource={explanationList}
                        loading={loading}
                        columns={[
                            {
                                title: t("Document"),
                                dataIndex: "doc_id",
                                key: "doc_id",
                                width: 120,
                                ellipsis: true,
                                render: (doc_id: string, record: CategorizationResult) => (
                                    <a
                                        href={record.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:text-blue-300 underline"
                                    >
                                        {doc_id}
                                    </a>
                                ),
                            },
                            {
                                title: t("Product"),
                                dataIndex: "product",
                                key: "product",
                                width: 120,
                                ellipsis: true,
                                sorter: (a, b) => a.product.localeCompare(b.product),
                                filtered: true,
                                filters: productFilters,
                                onFilter: (value, record) => record.product === value,
                            },
                            {
                                title: t("Version"),
                                dataIndex: "version",
                                key: "version",
                                width: 90,
                                ellipsis: true,
                                sorter: (a, b) => a.version.localeCompare(b.version),
                                filtered: true,
                                filters: versionFilters,
                                onFilter: (value, record) => record.version === value,
                            },
                            {
                                title: t("Explanation"),
                                dataIndex: "explanation",
                                key: "explanation",
                            },
                        ]}
                        rowKey="doc_id"
                        pagination={false}
                        prefixCls={"arten-search"}
                        rowClassName={"case-filter-table-row"}
                        className={"case-filter-table"}
                        rootClassName={"case-filter-table-root"}
                        bordered={false}
                        sortDirections={["ascend", "descend"]}
                        showSorterTooltip={true}
                        components={{
                            header: {
                                wrapper: (props: ReactPropTypes) => (
                                    <thead {...props} className={"case-filter-table-header"} />
                                ),
                            },
                        }}
                    />
                </div>
            )}
        </div>
    );
}
